// DeepL API integration for automatic translations
// You need to set REACT_APP_DEEPL_API_KEY in your .env file

import { devLog, devWarn } from '../utils/devLog';
import { tokenService } from '../services/tokenService';

const DEEPL_API_KEY = process.env.REACT_APP_DEEPL_API_KEY;
const DEEPL_API_URL = 'https://api.deepl.com/v2/translate';
// Issue #357: fest verdrahtete Remote-URL ersetzt durch die konfigurierte
// SSO-Basis (lokal → localhost). Fallback bleibt die Prod-SSO.
const SSO_BASE = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
const BACKEND_TRANSLATE_URL = `${SSO_BASE.replace(/\/$/, '')}/api/translate`;

export interface TranslationRequest {
    text: string;
    sourceLang: string;
    targetLang: string;
}

export interface TranslationResponse {
    translations: Array<{
        detected_source_language: string;
        text: string;
    }>;
}

/**
 * Translate text using DeepL API
 * @param text Text to translate
 * @param targetLang Target language (DE, EN, etc.)
 * @param sourceLang Source language (optional, auto-detect if not provided)
 * @returns Translated text
 */
export async function translateWithDeepL(
    text: string,
    targetLang: string,
    sourceLang?: string
): Promise<string> {
    if (!DEEPL_API_KEY) {
        devWarn('[DeepL] API key not configured. Skipping automatic translation.');
        return text;
    }

    try {
        devLog(`[DeepL] Translating "${text.substring(0, 50)}..." to ${targetLang}`);

        const params = new URLSearchParams({
            auth_key: DEEPL_API_KEY,
            text: text,
            target_lang: targetLang.toUpperCase(),
        });

        if (sourceLang) {
            params.append('source_lang', sourceLang.toUpperCase());
        }

        const response = await fetch(DEEPL_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: params.toString(),
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`[DeepL] API error (${response.status}):`, errorText);
            throw new Error(`DeepL API error: ${response.statusText}`);
        }

        const data: TranslationResponse = await response.json();
        const translated = data.translations[0]?.text || text;
        devLog(`[DeepL] Result: "${translated.substring(0, 50)}..."`);
        return translated;
    } catch (error) {
        console.error('[DeepL] Translation error:', error);
        return text;
    }
}

/**
 * Schützt Platzhalter wie %{name}, %{id}, |||| vor Übersetzung
 */
function protectPlaceholders(text: string): { protectedText: string; placeholders: Map<string, string> } {
    const placeholders = new Map<string, string>();
    let counter = 0;

    // Schütze Polyglot-Variablen: %{variable}
    let protectedText = text.replace(/%\{[^}]+\}/g, (match) => {
        const placeholder = `__PLACEHOLDER_${counter}__`;
        placeholders.set(placeholder, match);
        counter++;
        return placeholder;
    });

    // Schütze Polyglot-Plural-Separator: ||||
    protectedText = protectedText.replace(/\|\|\|\|/g, () => {
        const placeholder = `__PLACEHOLDER_${counter}__`;
        placeholders.set(placeholder, '||||');
        counter++;
        return placeholder;
    });

    return { protectedText, placeholders };
}

/**
 * Stellt geschützte Platzhalter wieder her
 */
function restorePlaceholders(text: string, placeholders: Map<string, string>): string {
    let restored = text;
    placeholders.forEach((original, placeholder) => {
        restored = restored.replace(new RegExp(placeholder, 'g'), original);
    });
    return restored;
}

/**
 * Translate multiple texts at once
 * @param texts Array of texts to translate
 * @param targetLang Target language
 * @param sourceLang Source language (optional)
 * @returns Array of translated texts
 */
export async function translateBatchWithDeepL(
    texts: string[],
    targetLang: string,
    sourceLang?: string
): Promise<string[]> {
    try {
        devLog(`[DeepL Batch] Translating ${texts.length} texts to ${targetLang} via backend`);

        // Schütze Platzhalter vor Übersetzung
        const protectedTexts = texts.map(text => protectPlaceholders(text));
        const textsToTranslate = protectedTexts.map(pt => pt.protectedText);

        // Verwende Backend-Proxy um CORS zu umgehen. Issue #357: der Endpoint
        // ist jetzt authentifiziert — Bearer-Token anhängen (UI-Übersetzung
        // läuft immer im eingeloggten Zustand).
        const token = tokenService.getToken();
        const response = await fetch(BACKEND_TRANSLATE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
                texts: textsToTranslate,
                targetLang: targetLang.toUpperCase(),
                sourceLang: sourceLang ? sourceLang.toUpperCase() : undefined,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            console.error(`[DeepL Batch] Backend error (${response.status}):`, errorData);
            throw new Error(`Backend translation error: ${errorData.error || response.statusText}`);
        }

        const data = await response.json();
        const translations = data.translations || [];

        // Stelle Platzhalter wieder her
        const restoredTranslations = translations.map((translation: string, index: number) => {
            return restorePlaceholders(translation, protectedTexts[index].placeholders);
        });

        devLog(`[DeepL Batch] Successfully translated ${restoredTranslations.length} texts`);
        devLog(`[DeepL Batch] Sample: "${texts[0]?.substring(0, 30)}..." -> "${restoredTranslations[0]?.substring(0, 30)}..."`);

        return restoredTranslations;
    } catch (error) {
        console.error('[DeepL Batch] Translation error:', error);
        return texts;
    }
}

/**
 * Helper to translate missing keys automatically
 * This can be used to fill in missing translations during development
 */
export async function autoTranslateMissingKeys(
    sourceTranslations: Record<string, any>,
    targetTranslations: Record<string, any>,
    targetLang: string,
    sourceLang: string = 'EN'
): Promise<Record<string, any>> {
    const result = { ...targetTranslations };

    const findMissingKeys = (
        source: Record<string, any>,
        target: Record<string, any>,
        path: string[] = []
    ): Array<{ path: string[]; value: string }> => {
        const missing: Array<{ path: string[]; value: string }> = [];

        for (const key in source) {
            const currentPath = [...path, key];

            if (typeof source[key] === 'object' && source[key] !== null) {
                if (!target[key] || typeof target[key] !== 'object') {
                    target[key] = {};
                }
                missing.push(...findMissingKeys(source[key], target[key], currentPath));
            } else if (typeof source[key] === 'string') {
                if (!target[key]) {
                    missing.push({ path: currentPath, value: source[key] });
                }
            }
        }

        return missing;
    };

    const missingKeys = findMissingKeys(sourceTranslations, result);

    if (missingKeys.length > 0) {
        devLog(`Found ${missingKeys.length} missing translation keys. Translating...`);

        // Translate in batches to avoid API limits
        const batchSize = 50;
        for (let i = 0; i < missingKeys.length; i += batchSize) {
            const batch = missingKeys.slice(i, i + batchSize);
            const textsToTranslate = batch.map(item => item.value);

            const translations = await translateBatchWithDeepL(
                textsToTranslate,
                targetLang,
                sourceLang
            );

            batch.forEach((item, index) => {
                let current: any = result;
                for (let j = 0; j < item.path.length - 1; j++) {
                    current = current[item.path[j]];
                }
                current[item.path[item.path.length - 1]] = translations[index];
            });
        }

        devLog('Automatic translation completed.');
    }

    return result;
}
